import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

import { AuthMeResponse, SessionSnapshot, StoredTokenPair, TokenResponse } from './auth.models';

const APP_STORAGE_PREFIX = 'badge.';

@Injectable({ providedIn: 'root' })
export class SessionService {
  private currentUser: AuthMeResponse | null = null;
  private sessionExpiresAtMs: number | null = null;

  setTokenPair(tokens: TokenResponse): void {
    if (typeof tokens.refresh_expires_in === 'number' && Number.isFinite(tokens.refresh_expires_in)) {
      this.sessionExpiresAtMs = Date.now() + Math.max(0, Math.floor(tokens.refresh_expires_in * 1000));
      this.debugLog('setTokenPair from refresh_expires_in', {
        refresh_expires_in: tokens.refresh_expires_in,
        computed_session_expires_at_ms: this.sessionExpiresAtMs,
        computed_session_expires_at_iso: this.sessionExpiresAtMs ? new Date(this.sessionExpiresAtMs).toISOString() : null,
      });
      return;
    }

    const expiryValue = tokens.refresh_expires_at || tokens.session_expires_at;
    const parsed = Date.parse(this.normalizeIsoUtc(expiryValue));
    this.sessionExpiresAtMs = Number.isFinite(parsed) ? parsed : null;
    this.debugLog('setTokenPair from refresh_expires_at/session_expires_at', {
      refresh_expires_at: tokens.refresh_expires_at,
      session_expires_at: tokens.session_expires_at,
      computed_session_expires_at_ms: this.sessionExpiresAtMs,
      computed_session_expires_at_iso: this.sessionExpiresAtMs ? new Date(this.sessionExpiresAtMs).toISOString() : null,
    });
  }

  getTokenPair(): StoredTokenPair | null {
    return null;
  }

  getAccessToken(): string | null {
    return null;
  }

  getRefreshToken(): string | null {
    return null;
  }

  hasValidAccessToken(): boolean {
    return this.getCurrentUser() !== null;
  }

  setCurrentUser(me: AuthMeResponse): void {
    this.currentUser = me;
  }

  getCurrentUser(): AuthMeResponse | null {
    return this.currentUser;
  }

  clearCurrentUser(): void {
    this.currentUser = null;
    this.removeLegacyAuthMeStorage();
  }

  clearTokens(): void {
    // Web auth now uses HttpOnly cookies managed by backend.
    // No token is persisted in browser storage.
  }

  clearSession(): void {
    this.clearTokens();
    this.sessionExpiresAtMs = null;
    this.clearAppStorageNamespace();
  }

  getSessionExpiresAtMs(): number | null {
    return this.sessionExpiresAtMs;
  }

  getSnapshot(): SessionSnapshot {
    return {
      tokens: this.getTokenPair(),
      me: this.getCurrentUser()
    };
  }

  private remove(key: string): void {
    const storage = this.storage;
    if (!storage) {
      return;
    }

    storage.removeItem(key);
  }

  private get storage(): Storage | null {
    if (typeof window === 'undefined') {
      return null;
    }

    return window.localStorage;
  }

  private clearAppStorageNamespace(): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.clearByPrefix(window.localStorage, APP_STORAGE_PREFIX);
    this.clearByPrefix(window.sessionStorage, APP_STORAGE_PREFIX);
  }

  private clearByPrefix(storage: Storage, prefix: string): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }

    for (const key of keysToRemove) {
      storage.removeItem(key);
    }
  }

  private removeLegacyAuthMeStorage(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.removeItem('badge.auth.me');
  }

  private normalizeIsoUtc(value: string): string {
    const normalized = value.trim();
    if (!normalized) {
      return normalized;
    }
    if (/[zZ]$/.test(normalized) || /[+-]\d{2}:\d{2}$/.test(normalized)) {
      return normalized;
    }
    return `${normalized}Z`;
  }

  private debugLog(message: string, payload?: unknown): void {
    if (environment.production) {
      return;
    }
    console.warn(`[SessionService] ${message}`, payload ?? null);
  }
}
